import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { BucketFileStatusEnum, IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { isNativeUrn } from 'Data/interfaces/Viewer/INativeViewerArtifacts';

// A página federada é urn-centric: a identidade do modelo na sessão é o urn da
// Autodesk (`urn:${BucketFile.Urn}`), que só existe depois da conversão. O nosso
// viewer não depende dela — lê os artefatos do render nativo. O IFC no nosso motor
// entra sempre pela urn sintética, derivada do identificador do arquivo: assim a
// identidade não muda quando a Autodesk converte o mesmo arquivo no meio da
// sessão, e nada mais na página precisa deixar de ser urn-centric.
export const IFC_URN_PREFIX = 'ifc:';

export const isIfcUrn = (urn: string): boolean => urn.startsWith(IFC_URN_PREFIX);

export const ifcUrnForFile = (file: IFileData): string =>
  `${IFC_URN_PREFIX}${file.Identifier?.ApiId || file.Identifier?.ItemId}`;

// Com as duas conversões lado a lado (`AutodeskStatus` presente), `Urn`/`Status`
// são os da conversão mostrada, que é a nativa quando ela está pronta; a da
// Autodesk vem nos campos dela. Backend anterior só tem a mostrada.
const hasConversionLanes = (file: IFileData): boolean => (
  file.FileInfo?.BucketFile?.AutodeskStatus !== undefined
);

// Render nativo (`native:{sha256}`) não é urn da Autodesk.
const autodeskUrnOf = (file: IFileData): string | undefined => {
  const bucketFile = file.FileInfo?.BucketFile;
  if (hasConversionLanes(file)) { return bucketFile?.AutodeskUrn || undefined; }
  const urn = bucketFile?.Urn;
  return urn && !isNativeUrn(urn) ? urn : undefined;
};

/** Status da conversão da Autodesk: o que a sessão da Autodesk abre ou converte. */
export const autodeskStatusOfFile = (file: IFileData): BucketFileStatusEnum | null | undefined => {
  const bucketFile = file.FileInfo?.BucketFile;
  if (hasConversionLanes(file)) { return bucketFile?.AutodeskStatus ?? BucketFileStatusEnum.NotRendered; }
  if (bucketFile?.Status === BucketFileStatusEnum.Rendered && isNativeUrn(bucketFile.Urn)) {
    return BucketFileStatusEnum.NotRendered;
  }
  return bucketFile?.Status;
};

/**
 * IFC com render nativo pronto: o único estado em que ele entra na cena do nosso
 * motor. `NativeStatus` vale mesmo com a Autodesk convertendo por cima.
 */
export const isNativeReadyFile = (file: IFileData): boolean => {
  const bucketFile = file.FileInfo?.BucketFile;
  if (bucketFile?.NativeStatus !== undefined) { return bucketFile.NativeStatus === BucketFileStatusEnum.Rendered; }
  return bucketFile?.Status === BucketFileStatusEnum.Rendered && isNativeUrn(bucketFile?.Urn);
};

/** Uma conversão por vez: com uma andando, não se pede a do outro motor. */
export const isConvertingFile = (file: IFileData): boolean => {
  const bucketFile = file.FileInfo?.BucketFile;
  return bucketFile?.Status === BucketFileStatusEnum.Rendering
    || bucketFile?.NativeStatus === BucketFileStatusEnum.Rendering
    || bucketFile?.AutodeskStatus === BucketFileStatusEnum.Rendering;
};

// urn do arquivo numa sessão da Autodesk: só a da conversão dela; sem ela, nada
// a abrir (a sintética e a `native:` não dizem nada para o Forge).
export const autodeskSessionUrn = (file: IFileData): string => {
  const autodeskUrn = autodeskUrnOf(file);
  return autodeskUrn ? `urn:${autodeskUrn}` : '';
};

const matchesUrn = (file: IFileData, urn: string): boolean => {
  const bucketFile = file.FileInfo?.BucketFile;
  return `urn:${bucketFile?.Urn}` === urn || (!!bucketFile?.AutodeskUrn && `urn:${bucketFile.AutodeskUrn}` === urn);
};

// urn ativo → arquivo da lista federada (aceita os dois formatos de urn).
export const resolveUrnToFile = (
  urn: string,
  disciplines: IDisciplineViewerList[],
): IFileData | undefined => {
  const synthetic = isIfcUrn(urn);
  for (const discipline of disciplines) {
    const file = discipline.FilesList?.find((f) => (synthetic ? ifcUrnForFile(f) === urn : matchesUrn(f, urn)));
    if (file) { return file; }
  }
  return undefined;
};
